import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { NewsTitleContent } from '@sorare/core/src/components/player/News/NewsTitleContent';
import { NewsUpdates } from '@sorare/core/src/components/player/News/NewsUpdates';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Header } from '@sorare/core/src/components/seo/Header';
import { MetricProps } from '@sorare/core/src/components/seo/HeaderMetrics';
import { CardSupply } from '@sorare/core/src/components/seo/Player/CardSupply';
import { Infos } from '@sorare/core/src/components/seo/Player/Infos';
import { Country } from '@sorare/core/src/components/seo/Player/Infos/Country';
import { NBA_TEAMS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { NBA_HOME } from '@sorare/core/src/constants/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  cardAttributes,
  filters,
  glossary,
} from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  getPlayerInjuryColor,
  getPlayerInjuryMessage,
} from '@sorare/core/src/lib/nba';

import { PriceGraph } from '@sorare/marketplace/src/components/price/PriceGraph';

import { LazyFixtureChart } from '@sorare/gameplay/src/components/stats/LastScores/LazyFixtureChart';

import { GamesNavigator } from 'components/GamesNavigator';

import {
  NBAPlayerOverviewQuery,
  NBAPlayerOverviewQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const messages = defineMessages({
  avgMinutes: {
    id: 'Nba.Seo.Players.Slug.Overview.averageMinutesPlayed',
    defaultMessage: 'AVG {minutes}mn played',
  },
  injuryStatus: {
    id: 'Nba.Seo.Players.Slug.Overview.injuryStatus',
    defaultMessage: 'Injury Status',
  },
  injuryReturn: {
    id: 'Nba.Seo.Players.Slug.Overview.injuryMessage',
    defaultMessage: 'Est. Return {returnDate} ({description})',
  },
});

const NBA_PLAYER_OVERVIEW_QUERY = gql`
  query NBAPlayerOverviewQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      squaredPictureUrl
      displayName
      age
      shirtNumber
      cardSupply {
        season {
          startYear
        }
      }
      activeClub {
        id
        slug
        name
        pictureUrl
      }
      country {
        slug
        code
        threeLetterCode
        name
      }
      activeInjuries {
        id
        status
        kind
        details
        startDate
        expectedEndDate
      }
      ...CardSupply_anyPlayerInterface
      ...PlayerScore_anyPlayer
      ...Header_subscription
      news(last: 10) {
        ...NewsUpdates_PlayerNewsUpdate
      }
    }
  }
  ${NewsUpdates.fragments.PlayerNewsUpdate}
  ${CardSupply.fragments.anyPlayer}
  ${Header.fragments.subscription}
  ${PlayerScore.fragments.anyPlayer}
` as TypedDocumentNode<NBAPlayerOverviewQuery, NBAPlayerOverviewQueryVariables>;

const Overview = () => {
  const { slug } = useTypedParams<PageParams>();
  const { formatDate } = useIntlContext();
  const { formatMessage } = useIntl();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { data, loading } = useQuery(NBA_PLAYER_OVERVIEW_QUERY, {
    variables: { slug },
    skip: !slug,
  });

  if (loading) return <LoadingIndicator fullHeight />;
  const nbaPlayer = data?.anyPlayer;
  if (!isType(nbaPlayer, 'NBAPlayer')) return null;

  let injury: MetricProps | undefined;
  if (nbaPlayer.activeInjuries.length) {
    const {
      status,
      kind: description,
      expectedEndDate,
    } = nbaPlayer.activeInjuries[0];

    injury = {
      label: <FormattedMessage {...messages.injuryStatus} />,
      color: `var(--c-${getPlayerInjuryColor(status)}-600)`,
      body: (
        <FormattedMessage {...getPlayerInjuryMessage(status, { long: true })} />
      ),
      subLabel: expectedEndDate ? (
        <FormattedMessage
          {...messages.injuryReturn}
          values={{
            description,
            returnDate: formatDate(expectedEndDate),
          }}
        />
      ) : (
        description
      ),
    };
  }

  const playersBreadcrumb = [
    {
      to: NBA_HOME,
      name: 'NBA',
    },
    {
      name: formatMessage(glossary.players),
    },
    { name: nbaPlayer.displayName },
  ];

  const blocks = {
    header: (
      <>
        <Breadcrumb sections={playersBreadcrumb} />
        <Header
          id={nbaPlayer.slug}
          displayName={nbaPlayer.displayName}
          pictureUrl={nbaPlayer.squaredPictureUrl}
          memberships={[
            ...(nbaPlayer.activeClub
              ? [
                  {
                    name: nbaPlayer.activeClub.name,
                    pictureUrl: nbaPlayer.activeClub.pictureUrl,
                    to: generatePath(NBA_TEAMS_SLUG, {
                      slug: nbaPlayer.activeClub.slug,
                    }),
                  },
                ]
              : []),
          ]}
          scores={[
            {
              label: <FormattedMessage {...cardAttributes.last10} />,
              // [US-512] TODO RESTORE WHEN WE HAVE A RAILS ATTRIBUTE FOR MINUTES PLAYED
              // subLabel: !!nbaPlayer.tenGameAverageGameStats.detailedStats && (
              //   <FormattedMessage
              //     {...messages.avgMinutes}
              //     values={{
              //       minutes: (
              //         <FormattedNumber
              //           value={
              //             nbaPlayer.tenGameAverageGameStats.detailedStats
              //               .secondsPlayed / 60
              //           }
              //           maximumFractionDigits={0}
              //         />
              //       ),
              //     }}
              //   />
              // ),
              body: (
                <PlayerScore
                  mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
                  player={nbaPlayer}
                />
              ),
            },
          ].filter(Boolean)}
          subscription={nbaPlayer}
          injury={injury}
        />
      </>
    ),
    infos: (
      <Infos
        informations={[
          {
            label: cardAttributes.jerseyNumber,
            value: <>#{nbaPlayer.shirtNumber}</>,
          },
          {
            label: cardAttributes.age,
            value: nbaPlayer.age,
          },
          {
            label: filters.birthplace,
            value: (
              <Country
                code={nbaPlayer.country.code}
                threeLetterCode={nbaPlayer.country.threeLetterCode}
              />
            ),
          },
        ].filter(Boolean)}
      />
    ),
    news: (
      <FullWidthContainer>
        <Vertical gap={1.5}>
          <HeadlineS as="h2">
            <NewsTitleContent />
          </HeadlineS>
          <NewsUpdates
            updates={nbaPlayer.news}
            playerName={nbaPlayer.displayName}
          />
        </Vertical>
      </FullWidthContainer>
    ),
    performance: (
      <FullWidthContainer>
        <LazyFixtureChart
          TitleComponent={HeadlineS}
          playerSlug={nbaPlayer.slug}
        />
      </FullWidthContainer>
    ),
    fixtures: <GamesNavigator playerSlug={nbaPlayer.slug} />,
    cardsSupply: <CardSupply anyPlayer={nbaPlayer} />,
    priceGraph: (
      <PriceGraph playerSlug={nbaPlayer.slug} withSeasonEligibility />
    ),
  };
  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[
          blocks.infos,
          ...(nbaPlayer.cardSupply?.length ? [blocks.cardsSupply] : []),
          blocks.news,
        ]}
      >
        {blocks.performance}
        {blocks.priceGraph}
        {blocks.fixtures}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.infos}
      {nbaPlayer.cardSupply?.length ? blocks.cardsSupply : undefined}
      {blocks.news}
      {blocks.performance}
      {blocks.priceGraph}
      {blocks.fixtures}
    </Section>
  );
};

export default Overview;
